/**
 * CreditGuardHost — the ONE global surface for AI-credit outages.
 *
 * Mounted once in the shell (DashboardLayout). It listens for the
 * `psn:ai-credits-insufficient` window event that the API client fires on ANY
 * 402/insufficient_ai_credits (lib/api/client.ts) — so no matter which feature
 * ran the AI call (wizard, composer, studio, copilot, account-intelligence,
 * prospection, CRM enrich, email drafting…), the user gets the SAME clear
 * message + a one-click path to refill. It shows a modal (feature that was
 * blocked, credits required vs available, current balance) with a "Recharger"
 * CTA that deep-links to the buy-credits flow, plus a toast for a lightweight
 * heads-up. It also keeps the shared wallet fresh (badge + settings read it).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  AI_CREDITS_INSUFFICIENT_EVENT,
  rememberBlockedRun,
  type NormalizedInsufficientCredits,
} from '@/lib/api/ai-credits';
import { Modal, useToast } from '@/ui';
import { AI_CREDITS_UPDATED_EVENT, useCreditsStore } from './useCreditsStore';
import { featureLabel } from './featureLabels';

const REFRESH_MS = 60_000;

function fmtCredits(value: number | undefined | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 4 });
}

export function CreditGuardHost() {
  const navigate = useNavigate();
  const toast = useToast();
  const { wallet, refresh } = useCreditsStore();
  const [pending, setPending] = useState<NormalizedInsufficientCredits | null>(null);
  // Avoid a toast storm when several AI calls 402 back-to-back.
  const lastToastAt = useRef(0);
  // Warn once per low-balance episode (proactive nudge — the polling below picks
  // up a low balance without needing the backend's realtime event).
  const lowWarned = useRef(false);

  // Keep the wallet fresh: on mount, on a poll, and whenever anything asks.
  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), REFRESH_MS);
    const onUpdated = () => void refresh({ force: true });
    window.addEventListener(AI_CREDITS_UPDATED_EVENT, onUpdated);
    return () => {
      window.clearInterval(id);
      window.removeEventListener(AI_CREDITS_UPDATED_EVENT, onUpdated);
    };
  }, [refresh]);

  // The global 402 listener: open the refill modal + a throttled toast, and
  // re-fetch the wallet (the reservation may have moved the numbers).
  useEffect(() => {
    const onInsufficient = (e: Event) => {
      const detail = (e as CustomEvent<NormalizedInsufficientCredits>).detail ?? {};
      setPending(detail);
      // L-resume: remember the blocked run so the settings page can offer
      // "Recharger et continuer" for THIS run after the Stripe top-up.
      rememberBlockedRun(detail.blockedRunId);
      void refresh({ force: true });
      const now = Date.now();
      if (now - lastToastAt.current > 4000) {
        lastToastAt.current = now;
        toast.error(detail.detail || 'Crédits IA insuffisants — rechargez votre solde', 6000);
      }
    };
    window.addEventListener(AI_CREDITS_INSUFFICIENT_EVENT, onInsufficient);
    return () => window.removeEventListener(AI_CREDITS_INSUFFICIENT_EVENT, onInsufficient);
  }, [refresh, toast]);

  // Proactive low-balance nudge: one toast when the wallet crosses into "low"
  // (available at/under its threshold); re-arms once it's topped back up.
  useEffect(() => {
    if (!wallet) return;
    const low = wallet.available <= (wallet.low_balance_threshold ?? 0);
    if (low && !lowWarned.current) {
      lowWarned.current = true;
      toast.info('Solde de crédits IA faible — pensez à recharger votre portefeuille.', 7000);
    } else if (!low) {
      lowWarned.current = false;
    }
  }, [wallet, toast]);

  if (!pending) return null;

  const available = pending.available ?? wallet?.available ?? 0;
  const required = pending.required ?? 0;
  const missing = Math.max(required - available, 0);
  const resumable = Boolean(pending.blockedRunId);

  const goRefill = () => {
    setPending(null);
    // ?resume=1 nudges the settings page to offer resuming the blocked run
    // (which it reads from localStorage) once the top-up completes.
    navigate(`/settings/credits?buy=1${resumable ? '&resume=1' : ''}`);
  };

  return (
    <Modal
      title="Crédits IA insuffisants"
      size="md"
      onClose={() => setPending(null)}
      footer={
        <>
          <button
            type="button"
            onClick={() => setPending(null)}
            className="rounded-token border border-border bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Plus tard
          </button>
          <button
            type="button"
            onClick={goRefill}
            className="rounded-token bg-gradient-to-br from-primary to-accent px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-elevation-1 transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface"
          >
            {resumable ? 'Recharger et continuer' : 'Recharger mes crédits'}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-foreground">
          {pending.detail || 'Votre solde de crédits IA ne couvre pas cette opération.'}
        </p>
        <p className="text-muted">
          Pour poursuivre{' '}
          <span className="font-medium text-foreground">
            {pending.feature ? featureLabel(pending.feature) : 'cette action IA'}
          </span>
          , il faut recharger votre portefeuille de crédits IA.
        </p>
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Requis" value={`${fmtCredits(required)} cr`} tone="warning" />
          <Stat label="Disponible" value={`${fmtCredits(available)} cr`} tone="danger" />
          <Stat label="Manquant" value={`${fmtCredits(missing)} cr`} tone="primary" />
        </div>
        <p className="text-xs text-muted">
          Aucun crédit n’a été débité — l’appel IA n’a pas été exécuté.
          {resumable
            ? ' Après la recharge, votre action reprendra automatiquement.'
            : ' Rechargez pour réessayer.'}
        </p>
      </div>
    </Modal>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: 'warning' | 'danger' | 'primary' }) {
  const toneClass =
    tone === 'danger'
      ? 'text-danger'
      : tone === 'warning'
        ? 'text-warning'
        : 'text-primary';
  return (
    <div className="rounded-token border border-border bg-surface-2/40 px-2 py-2 text-center">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className={`mt-0.5 text-sm font-bold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}
